import { useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://bluecactus.digital';
const OG_IMAGE = 'https://customer-assets.emergentagent.com/job_0b608c3c-f868-434e-b1aa-578790eaab8e/artifacts/o5o9ncsr_blue%20cactus%20logo%20teal%20rgb.png';

const seoDefaults = {
  '/': { title: 'Blue Cactus Digital | Digital Marketing Agency for Ambitious Businesses', description: 'A specialist digital marketing agency, rooted in health and social care and now working with businesses in every sector, from professional services and tech to education and charities. SEO, paid ads, websites and branding that grow your reach.' },
  '/about': { title: 'About Us | Blue Cactus Digital Marketing Agency', description: 'Meet the team at Blue Cactus Digital. Founded by Clare Martin with deep health and social care roots, we now bring that same rigour to ambitious businesses across every sector.' },
  '/digital-marketing': { title: 'Digital Marketing Agency | SEO, Paid Ads & Social | Blue Cactus Digital', description: 'SEO, paid advertising, social media and communications strategy for businesses in any sector, from professional services and tech and SaaS to education, charities, care providers and healthtech.' },
  '/seo': { title: 'SEO, Content & Paid Ads Agency | Blue Cactus Digital', description: 'Get found by the people who need you most. SEO, content and paid advertising that grows your reach month after month, for businesses in any sector.' },
  '/websites': { title: 'Website Design & Development for Any Sector | Blue Cactus Digital', description: 'We design and build websites that make your services easier to find, understand and access, for professional services, tech and SaaS, e-commerce, education, charities and health and care.' },
  '/branding': { title: 'Branding & Graphic Design Agency | Blue Cactus Digital', description: 'Brand identity, visual guidelines and design systems for ambitious businesses in any sector, from professional services and tech startups to charities, care providers and healthtech companies.' },
  '/contact': { title: 'Contact Blue Cactus Digital | Digital Marketing Agency', description: 'Get in touch to discuss your marketing, whatever your sector. We work with professional services, tech, education, charities, care providers and healthtech companies across the UK.' },
  '/blog': { title: 'Marketing Blog | Blue Cactus Digital', description: 'Practical digital marketing advice for care homes, home care agencies, healthtech companies and health charities. Tips on SEO, social media and more.' },
  '/book-online': { title: 'Book a Free Planning Session | Blue Cactus Digital', description: 'Book a free 30-minute call with Clare Martin to discuss your marketing goals and how Blue Cactus Digital can help.' },
  '/quiz': { title: 'Marketing Quiz for Health and Social Care | Blue Cactus Digital', description: 'Take our quick quiz to discover which marketing services are right for your health or social care organisation.' },
  '/quiz/thank-you': { title: 'Your Results | Blue Cactus Digital Marketing Quiz', description: 'Your personalised marketing recommendation from Blue Cactus Digital.' },
};

const setOrCreateMeta = (attr, attrValue, content) => {
  let el = document.querySelector(`meta[${attr}="${attrValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (url) => {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
};

const PageSeo = ({ path, title: customTitle, description: customDesc, skipCmsOverride = false, noindex = false }) => {
  useEffect(() => {
    const defaults = seoDefaults[path] || {};
    const pageTitle = customTitle || defaults.title || 'Blue Cactus Digital | Marketing for Health and Social Care';
    const pageDesc = customDesc || defaults.description || 'Specialist digital marketing agency for health and social care.';
    const canonicalUrl = `${SITE_URL}${path || ''}`;

    // Title
    document.title = pageTitle;

    // Meta description
    setOrCreateMeta('name', 'description', pageDesc);

    // Robots (noindex for thin/utility/not-found pages, index otherwise)
    setOrCreateMeta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow');

    // Canonical
    setCanonical(canonicalUrl);

    // Open Graph
    setOrCreateMeta('property', 'og:title', pageTitle);
    setOrCreateMeta('property', 'og:description', pageDesc);
    setOrCreateMeta('property', 'og:url', canonicalUrl);
    setOrCreateMeta('property', 'og:type', 'website');
    setOrCreateMeta('property', 'og:site_name', 'Blue Cactus Digital');
    setOrCreateMeta('property', 'og:image', OG_IMAGE);

    // Twitter Card
    setOrCreateMeta('name', 'twitter:card', 'summary_large_image');
    setOrCreateMeta('name', 'twitter:title', pageTitle);
    setOrCreateMeta('name', 'twitter:description', pageDesc);
    setOrCreateMeta('name', 'twitter:image', OG_IMAGE);

    // Try CMS override (skipped when a dynamic ad message is driving the meta).
    if (skipCmsOverride || noindex) return;
    fetch(`${API}/api/content/seo?path=${encodeURIComponent(path)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          if (data.title) {
            document.title = data.title;
            setOrCreateMeta('property', 'og:title', data.title);
            setOrCreateMeta('name', 'twitter:title', data.title);
          }
          if (data.description) {
            setOrCreateMeta('name', 'description', data.description);
            setOrCreateMeta('property', 'og:description', data.description);
            setOrCreateMeta('name', 'twitter:description', data.description);
          }
        }
      })
      .catch(() => {});
  }, [path, customTitle, customDesc, skipCmsOverride, noindex]);

  return null;
};

export default PageSeo;
